import { useState, type ReactNode } from 'react';
import { CornerUpLeft, LoaderCircle, RefreshCw, X } from 'lucide-react';
import { DestinationIcon } from './DestinationRow';
import { actionsOf, nameOf, stateLabel, stateTone, whenOf, whereOf } from './export-history-words';
import type { PaigeDestination } from './paige.global';
import type { PaigeExportRow } from './export-types';

export interface ExportHistoryRowProps {
  row: PaigeExportRow;
  runsJobs: boolean;
  onAct: (action: 'retry' | 'cancel', row: PaigeExportRow) => Promise<void>;
  onReview: (row: PaigeExportRow) => void;
}

/**
 * One delivery in the export history: the document as it was filed, its type, where it went and when, its state, the
 * destination's own words when it failed, and Retry delivery, Cancel delivery and Send back for review where each
 * applies (export-history-words.ts, actionsOf). An action's refusal is said on the row, in Paige's words.
 */
export function ExportHistoryRow({ row, runsJobs, onAct, onReview }: ExportHistoryRowProps) {
  const [busy, setBusy] = useState<'retry' | 'cancel' | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const act = async (action: 'retry' | 'cancel') => {
    setBusy(action);
    setRefusal(null);
    try {
      await onAct(action, row);
    } catch (failure) {
      setRefusal(failure instanceof Error ? failure.message : 'Paige could not do that');
    } finally {
      setBusy(null);
    }
  };
  const tone = stateTone(row.state);
  return (
    <li className={`paige-export-row is-${tone}`}>
      <span className="paige-export-row-where" title={whereOf(row)}>
        <DestinationIcon
          kind={(row.destinationKind ?? 'download') as PaigeDestination['kind']}
          size={16}
        />
      </span>
      <RowText row={row} refusal={refusal} />
      <span className={`paige-export-state is-${tone}`}>{stateLabel(row)}</span>
      <RowActions
        row={row}
        runsJobs={runsJobs}
        busy={busy}
        onAct={(a) => void act(a)}
        onReview={onReview}
      />
    </li>
  );
}

function RowText({ row, refusal }: { row: PaigeExportRow; refusal: string | null }) {
  const { name, folders } = nameOf(row);
  return (
    <span className="paige-export-row-text">
      <b title={row.filed ?? undefined}>{name}</b>
      <small>
        {[row.type ?? 'No type', whereOf(row), whenOf(row.at)].join(' · ')}
        {folders && <span className="paige-export-row-folders"> · {folders}</span>}
      </small>
      {row.lastError && <small className="paige-export-row-error">{row.lastError}</small>}
      {refusal && (
        <small className="paige-export-row-error" role="alert">
          {refusal}
        </small>
      )}
    </span>
  );
}

interface ActionsProps {
  row: PaigeExportRow;
  runsJobs: boolean;
  busy: 'retry' | 'cancel' | null;
  onAct: (action: 'retry' | 'cancel') => void;
  onReview: (row: PaigeExportRow) => void;
}

function RowActions({ row, runsJobs, busy, onAct, onReview }: ActionsProps) {
  const actions = actionsOf(row, runsJobs);
  const { name } = nameOf(row);
  return (
    <span className="paige-export-row-actions">
      {actions.retry && (
        <Action
          busy={busy === 'retry'}
          disabled={busy !== null}
          label={`Retry delivery of ${name}`}
          icon={<RefreshCw size={12} aria-hidden="true" />}
          onClick={() => onAct('retry')}
        >
          {busy === 'retry' ? 'Retrying…' : 'Retry delivery'}
        </Action>
      )}
      {actions.cancel && (
        <Action
          busy={busy === 'cancel'}
          disabled={busy !== null}
          label={`Cancel delivery of ${name}`}
          icon={<X size={12} aria-hidden="true" />}
          onClick={() => onAct('cancel')}
        >
          {busy === 'cancel' ? 'Cancelling…' : 'Cancel delivery'}
        </Action>
      )}
      {actions.review && <ReviewAction name={name} onClick={() => onReview(row)} />}
    </span>
  );
}

/** Send back for review: the document opens in review, where a Confirm sends it to where it was refused (ADR-0032). */
function ReviewAction({ name, onClick }: { name: string; onClick: () => void }) {
  return (
    <Action
      busy={false}
      disabled={false}
      label={`Send ${name} back for review`}
      title="Opens it in review: correct it and confirm, and Paige sends it to where it was refused. Nowhere else."
      icon={<CornerUpLeft size={12} aria-hidden="true" />}
      onClick={onClick}
    >
      Send back for review
    </Action>
  );
}

function Action(props: {
  busy: boolean;
  disabled: boolean;
  label: string;
  title?: string;
  icon: ReactNode;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="quiet-button small"
      disabled={props.disabled}
      aria-label={props.label}
      title={props.title}
      onClick={props.onClick}
    >
      {props.busy ? (
        <LoaderCircle className="paige-spin" size={12} aria-hidden="true" />
      ) : (
        props.icon
      )}
      {props.children}
    </button>
  );
}
